import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Users, 
  Shield, 
  User,
  Loader2,
  Building2,
  UserCog,
  CheckCircle,
  XCircle,
  Clock,
  Inbox,
  UserPlus,
  ArrowLeft,
  AlertTriangle,
  Mail
} from 'lucide-react';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Requests() {
  const navigate = useNavigate();
  const { user: currentUser, isAdmin } = useAuth();
  const [pendingManagers, setPendingManagers] = useState([]);
  const [pendingOperatives, setPendingOperatives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [userToReject, setUserToReject] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [activeTab, setActiveTab] = useState('managers');

  useEffect(() => {
    if (!isAdmin || !isAdmin()) {
      navigate('/dashboard');
      return;
    }
    fetchPendingRequests();
  }, [isAdmin, navigate]);

  const fetchPendingRequests = async () => {
    setLoading(true);
    try {
      // Fetch pending managers
      const managersRes = await axios.get(`${API}/companies/pending-managers`);
      setPendingManagers(managersRes.data || []);
      
      // Fetch pending operatives (if endpoint exists)
      try {
        const operativesRes = await axios.get(`${API}/companies/pending-operatives`);
        setPendingOperatives(operativesRes.data || []);
      } catch (e) {
        // Endpoint might not exist yet
        setPendingOperatives([]);
      }
    } catch (error) {
      console.error('Failed to fetch pending requests:', error);
      toast.error('Failed to load pending requests');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (user, type) => {
    setProcessingId(user.user_id);
    try {
      const endpoint = type === 'manager' 
        ? `${API}/companies/approve-manager`
        : `${API}/companies/approve-operative`;
      
      await axios.post(endpoint, { user_id: user.user_id });
      
      if (type === 'manager') {
        setPendingManagers(pendingManagers.filter(m => m.user_id !== user.user_id));
      } else {
        setPendingOperatives(pendingOperatives.filter(o => o.user_id !== user.user_id));
      }
      
      toast.success(`${user.name} has been approved as ${type}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve request');
    } finally {
      setProcessingId(null);
    }
  };

  const openRejectDialog = (user, type) => {
    setUserToReject({ ...user, type });
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  const handleReject = async () => {
    if (!userToReject) return;
    
    setProcessingId(userToReject.user_id);
    try {
      const endpoint = userToReject.type === 'manager'
        ? `${API}/companies/reject-manager`
        : `${API}/companies/reject-operative`;
      
      await axios.post(endpoint, { 
        user_id: userToReject.user_id,
        reason: rejectReason || 'Request declined by admin'
      });
      
      if (userToReject.type === 'manager') {
        setPendingManagers(pendingManagers.filter(m => m.user_id !== userToReject.user_id));
      } else {
        setPendingOperatives(pendingOperatives.filter(o => o.user_id !== userToReject.user_id));
      }
      
      toast.success(`${userToReject.name}'s request has been declined`);
      setRejectDialogOpen(false);
      setUserToReject(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject request');
    } finally {
      setProcessingId(null);
    }
  };

  const totalPending = pendingManagers.length + pendingOperatives.length;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/dashboard')}
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="font-heading text-2xl font-bold text-slate-800 tracking-tight">
                PENDING REQUESTS
              </h1>
              <p className="text-slate-500 text-sm">
                Review and manage join requests from your team
              </p>
            </div>
          </div>
          {totalPending > 0 && (
            <Badge variant="secondary" className="text-base px-4 py-1">
              {totalPending} pending
            </Badge>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <UserCog className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Pending Managers</p>
                  <p className="text-xl font-bold">{pendingManagers.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                  <User className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Pending Operatives</p>
                  <p className="text-xl font-bold">{pendingOperatives.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Clock className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Total Pending</p>
                  <p className="text-xl font-bold">{totalPending}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {totalPending === 0 ? (
          <Card className="text-center py-16">
            <CardContent>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="font-heading text-xl font-semibold mb-2">All caught up!</h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                No pending requests at the moment. When managers or operatives request to join your company, they'll appear here.
              </p>
            </CardContent>
          </Card>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-6">
              <TabsTrigger value="managers" className="gap-2">
                <UserCog className="h-4 w-4" />
                Managers
                {pendingManagers.length > 0 && (
                  <Badge variant="secondary" className="ml-1">{pendingManagers.length}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="operatives" className="gap-2">
                <User className="h-4 w-4" />
                Operatives
                {pendingOperatives.length > 0 && (
                  <Badge variant="secondary" className="ml-1">{pendingOperatives.length}</Badge>
                )}
              </TabsTrigger>
            </TabsList>

            {/* Pending Managers Tab */}
            <TabsContent value="managers">
              {pendingManagers.length === 0 ? (
                <Card className="text-center py-12">
                  <CardContent>
                    <Inbox className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="font-heading text-lg font-semibold mb-2">No pending manager requests</h3>
                    <p className="text-muted-foreground">
                      Manager requests will appear here when they register
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardHeader>
                    <CardTitle className="font-heading text-lg">Manager Requests</CardTitle>
                    <CardDescription>
                      Managers can review, approve, and manage QA forms
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Site</TableHead>
                          <TableHead>Requested</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pendingManagers.map(manager => (
                          <TableRow key={manager.user_id}>
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                                  <UserCog className="h-4 w-4 text-blue-600" />
                                </div>
                                <span className="font-medium">{manager.name}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {manager.email}
                            </TableCell>
                            <TableCell>
                              {manager.site_name || '-'}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {manager.created_at 
                                ? format(new Date(manager.created_at), 'MMM d, yyyy')
                                : '-'
                              }
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => handleApprove(manager, 'manager')}
                                  disabled={processingId === manager.user_id}
                                  className="bg-green-600 hover:bg-green-700 gap-1"
                                  data-testid={`approve-manager-${manager.user_id}`}
                                >
                                  {processingId === manager.user_id ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <CheckCircle className="h-3 w-3" />
                                  )}
                                  Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openRejectDialog(manager, 'manager')}
                                  disabled={processingId === manager.user_id}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50 gap-1"
                                  data-testid={`reject-manager-${manager.user_id}`}
                                >
                                  <XCircle className="h-3 w-3" />
                                  Decline
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Pending Operatives Tab */}
            <TabsContent value="operatives">
              {pendingOperatives.length === 0 ? (
                <Card className="text-center py-12">
                  <CardContent>
                    <Inbox className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="font-heading text-lg font-semibold mb-2">No pending operative requests</h3>
                    <p className="text-muted-foreground">
                      Operative requests will appear here when they register
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardHeader>
                    <CardTitle className="font-heading text-lg">Operative Requests</CardTitle>
                    <CardDescription>
                      Operatives can create and submit QA forms
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Site</TableHead>
                          <TableHead>Requested</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pendingOperatives.map(operative => (
                          <TableRow key={operative.user_id}>
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
                                  <User className="h-4 w-4 text-orange-600" />
                                </div>
                                <span className="font-medium">{operative.name}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {operative.email}
                            </TableCell>
                            <TableCell>
                              {operative.site_name || '-'}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {operative.created_at 
                                ? format(new Date(operative.created_at), 'MMM d, yyyy')
                                : '-'
                              }
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => handleApprove(operative, 'operative')}
                                  disabled={processingId === operative.user_id}
                                  className="bg-green-600 hover:bg-green-700 gap-1"
                                  data-testid={`approve-operative-${operative.user_id}`}
                                >
                                  {processingId === operative.user_id ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <CheckCircle className="h-3 w-3" />
                                  )}
                                  Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openRejectDialog(operative, 'operative')}
                                  disabled={processingId === operative.user_id}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50 gap-1"
                                  data-testid={`reject-operative-${operative.user_id}`}
                                >
                                  <XCircle className="h-3 w-3" />
                                  Decline
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        )}

        {/* Reject Dialog */}
        <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                Decline Request
              </DialogTitle>
              <DialogDescription>
                You're about to decline {userToReject?.name}'s request to join as a {userToReject?.type}.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="reason">Reason (optional)</Label>
                <Textarea
                  id="reason"
                  placeholder="Provide a reason for declining this request..."
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  className="min-h-[100px]"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setRejectDialogOpen(false)}
                disabled={processingId === userToReject?.user_id}
              >
                Cancel
              </Button>
              <Button
                onClick={handleReject}
                disabled={processingId === userToReject?.user_id}
                className="bg-red-600 hover:bg-red-700"
              >
                {processingId === userToReject?.user_id ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Declining...
                  </>
                ) : (
                  'Decline Request'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
